import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import {
  AppointmentDataService,
  AppointmentModel,
  ClinicService,
  ConfirmService,
  CrudService, EstimateService,
  INSURANCE_PROVIDER_LOOKUP_TYPE,
  isInsuranceRelevantToAnyAccount,
  isPerVisitInsuranceSelectionEnabled,
  isSelfPayLikeInsurance,
  Patient,
  PATIENT_DATA_PROGRESS_KEY,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceStatus,
  PatientInsuranceType,
  PatientPalRestError,
  ProgressIndicatorService,
} from '@mmx/shared'
import { get, without } from 'lodash'
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs'

import { InsuranceDialogComponent } from '../insurance-dialog'
import { PatientInsuranceHistoryDialogComponent } from '../patient-insurance-history-dialog'
import { PullEligibilityDialogComponent, PullEligibilityDialogInput } from '../pull-eligibility-dialog'

@Component({
  selector: 'mmx-insurances-card',
  templateUrl: './insurances-card.component.html',
  standalone: false,
  styles: [`
    .insurances-card__empty {
      margin: 1rem 0;
      padding: 0 1rem;
      color: var(--text-secondary, rgba(0, 0, 0, 0.6));
    }

    .insurances-card__group-label {
      margin: 0.5rem 0;
      padding: 0 1rem;
      font-weight: 500;
      color: var(--text-secondary, rgba(0, 0, 0, 0.6));
    }

    .insurances-card__segment-toggle {
      /* mat-card-content is fxLayout="column", whose default cross-axis alignment
         (stretch) would otherwise force this to the card's full width */
      align-self: flex-start;
      margin: 0.5rem 1rem;
      font-size: 0.8125rem;
    }
  `],
})
export class InsurancesCardComponent implements OnInit, OnDestroy {
  @Input() embedded = false
  @Input() readOnly = false

  // Overrides the route/legacy-form appointment resolution below with the caller's own known
  // visit. `undefined` is the "unbound" sentinel; an explicit `null` still wins over the fallback.
  @Input() set visitAppointment(appointment: AppointmentModel | null) {
    this.visitAppointmentInput$.next(appointment)
  }
  private readonly visitAppointmentInput$ = new BehaviorSubject<AppointmentModel | null | undefined>(undefined)

  // Every selected visit (Course of Care) - widens "This Visit" to the union of their accounts.
  // Unbound/empty falls back to visitAppointment's single account.
  @Input() set visitAppointments(appointments: AppointmentModel[]) {
    this.visitAppointmentsInput$.next(appointments ?? [])
  }
  private readonly visitAppointmentsInput$ = new BehaviorSubject<AppointmentModel[]>([])

  public patient: Patient
  public appointment?: AppointmentModel
  public insuranceTableColumns = [
    'carrier',
    'status',
    'member',
    'type',
    'actions',
  ]

  // "Other" rows are read-only: editing one would send the current visit's account to the
  // save API, silently adding it onto a policy that belongs to a different visit.
  public get displayedColumns(): string[] {
    return this.readOnly || this.selectedSegment === 'other'
      ? this.insuranceTableColumns.filter(c => c !== 'actions')
      : this.insuranceTableColumns
  }
  public dataSource = new MatTableDataSource<PatientInsurance>([])
  public otherDataSource = new MatTableDataSource<PatientInsurance>([])

  // Resets to 'thisVisit' whenever the appointment changes, so switching patients/appointments
  // never leaves the user parked on a stale "other" view.
  public selectedSegment: 'thisVisit' | 'other' = 'thisVisit'

  public get showSegmentToggle(): boolean {
    return this.otherDataSource.data.length > 0
  }

  // resolveThisVisitType below can only resolve a per-visit role for exactly one account, so with
  // a multi-account selection the Type column shows the shared patient-level role - the label has
  // to say "selected visits" instead of promising one specific visit.
  public get isMultiAccountScope(): boolean {
    return (this.relevantAccountIds?.size ?? 0) > 1
  }

  // Set before insurancesChanged$ fires so that once filteredInsurances$ re-emits, we can reveal
  // whichever segment the saved insurance actually landed in instead of leaving it hidden.
  private pendingRevealInsuranceId: string | undefined

  public INSURANCE_PROVIDER_LOOKUP_TYPE = INSURANCE_PROVIDER_LOOKUP_TYPE
  public PatientInsuranceStatus = PatientInsuranceStatus
  public isSelfPayLikeInsurance = isSelfPayLikeInsurance

  public progress$ = this.patientDataService.progressService.inProgress(
    PATIENT_DATA_PROGRESS_KEY.PATIENT,
    PATIENT_DATA_PROGRESS_KEY.INSURANCES,
  )

  private onDestroy$ = new Subject<void>()

  private appointmentData$ = this.appointmentDataService.appointment$.pipe(
    takeUntil(this.onDestroy$),
  )

  private estimateAppointment$ = this.estimateService.allAppointments$.pipe(
    takeUntil(this.onDestroy$),
    // allAppointments$ emits null while loading (startWith sentinel); filter it here so the
    // genuine "no appointments" null (from an empty array) still reaches the scan as a valid
    // no-appointment signal, while the transient loading null never triggers a premature emission.
    filter((appointments): appointments is AppointmentModel[] => appointments !== null),
    map(appointments => appointments[0] ?? null),
  )

  private readonly routeOrLegacyAppointment$ = merge(
    this.appointmentData$.pipe(map(appointment => ({ source: 'appointment' as const, appointment }))),
    this.estimateAppointment$.pipe(map(appointment => ({ source: 'estimate' as const, appointment }))),
  ).pipe(
    // Prefer real appointment once it emits; ignore estimate emissions after that.
    scan((state, next) => {
      if (next.source === 'appointment') {
        return { hasAppointment: true, appointment: next.appointment }
      }
      if (state.hasAppointment) {
        return state
      }
      return { hasAppointment: false, appointment: next.appointment }
    }, { hasAppointment: false, appointment: null as AppointmentModel | null }),
    map((state) => state.appointment),
  )

  appointment$ = this.visitAppointmentInput$.pipe(
    // Compares the sentinel itself, not just ids - `undefined` (unbound, use the fallback) and
    // `null` (bound, nothing selected yet) both have no id but must not collapse into one.
    distinctUntilChanged((a, b) => (a === undefined) === (b === undefined) && a?.id === b?.id),
    switchMap(override => override === undefined ? this.routeOrLegacyAppointment$ : of(override)),
    shareReplay(1),
  )

  // ClinicService.clinic is a static populated by clinic$'s own HTTP call, which can land after
  // insurances$/appointment$ have both emitted - without this re-trigger the flag reads as off
  // once and every policy stays under "This visit" for the life of the card. Same guard
  // EstimateInsuranceLoader.attach() applies. startWith keeps the first emission's timing.
  private readonly clinicLoaded$ = this.clinicService.clinic$.pipe(startWith(null))

  /**
   * Union of accountIds relevant to the currently-selected visit(s), gated by the clinic's
   * per-visit-insurance-selection flag - mirrors EstimateInsuranceLoader.relevantAccountIds(),
   * which already applies this same "union of accounts" rule to insurance *selection* for
   * Course of Care estimates. Falls back to appointment$'s single account when
   * visitAppointments is empty/unbound, so callers that don't bind it see no change. `null`
   * means unscoped (matches every insurance), matching isInsuranceRelevantToAnyAccount.
   */
  private relevantAccountIds$ = combineLatest([
    this.appointment$,
    this.visitAppointmentsInput$,
    this.clinicLoaded$,
  ]).pipe(
    map(([appointment, visitAppointments]) => {
      if (!isPerVisitInsuranceSelectionEnabled(this.clinicService.clinic)) {
        return null
      }
      const accountIds = new Set(
        (visitAppointments.length > 0 ? visitAppointments : [appointment])
          .map(a => a?.accountId)
          .filter((id): id is string => !!id),
      )
      return accountIds.size > 0 ? accountIds : null
    }),
    shareReplay(1),
  )

  private relevantAccountIds: ReadonlySet<string> | null = null

  /**
   * Splits into "this visit" (accountIds intersects the relevant account(s), or has none
   * set) vs "other" insurances, but only on the appointment view of a flagged clinic.
   * Everywhere else - patient-level page, unflagged clinics, appointments without an
   * account - "this visit" is simply every insurance, matching prior behavior.
   */
  filteredInsurances$ = combineLatest([
    this.patientDataService.insurances$,
    this.relevantAccountIds$,
  ]).pipe(
    map(([insurances, accountIds]) => {
      const thisVisit = insurances.filter(insurance => isInsuranceRelevantToAnyAccount(insurance, accountIds))
      const other = insurances.filter(insurance => !thisVisit.includes(insurance))

      return { thisVisit, other }
    }),
  )

  /**
   * Same resolution appointment-eligibility-card.component.ts uses for the Pull Eligibility
   * preselect. "Other insurances" rows keep the raw shared `type` - no single account those
   * records resolve against. Falls back to the raw shared type (like
   * EstimateInsuranceLoader.accountAwareTypeAccessor) whenever more than one account is in
   * scope - a per-account role is only well-defined for exactly one account.
   */
  resolveThisVisitType(insurance: PatientInsurance): PatientInsuranceType {
    const perVisitEnabled = isPerVisitInsuranceSelectionEnabled(this.clinicService.clinic)
    const accountIds = this.relevantAccountIds
    const singleAccountId = accountIds?.size === 1 ? [...accountIds][0] : undefined
    return insurance.getAccountType(singleAccountId, perVisitEnabled)
  }

  constructor(
    private dialog: MatDialog,
    private patientDataService: PatientDataService,
    private confirm: ConfirmService,
    private crudService: CrudService,
    private appointmentDataService: AppointmentDataService,
    private estimateService: EstimateService,
    private progressService: ProgressIndicatorService,
    private clinicService: ClinicService,
  ) {}

  ngOnInit(): void {
    this.appointment$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((appointment) => {
        if (appointment?.id !== this.appointment?.id) {
          this.selectedSegment = 'thisVisit'
        }
        this.appointment = appointment ?? undefined
      })
    this.patientDataService.patient$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(patient => this.patient = patient)
    this.relevantAccountIds$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(accountIds => this.relevantAccountIds = accountIds)
    this.filteredInsurances$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ thisVisit, other }) => {
        this.dataSource.data = thisVisit
        this.otherDataSource.data = other

        if (this.pendingRevealInsuranceId) {
          if (other.some(insurance => insurance.id === this.pendingRevealInsuranceId)) {
            this.selectedSegment = 'other'
          } else if (thisVisit.some(insurance => insurance.id === this.pendingRevealInsuranceId)) {
            this.selectedSegment = 'thisVisit'
          }
          this.pendingRevealInsuranceId = undefined
        }
      })
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  /**
   * The full flat list regardless of "this visit" / "other" grouping - dialogs need
   * every policy on file (e.g. for duplicate-carrier checks), not just the visit subset.
   */
  private get allInsurances(): PatientInsurance[] {
    return [...this.dataSource.data, ...this.otherDataSource.data]
  }

  // add/edit/delete/pull below target the earliest selected visit only; per-visit edits for
  // visit 2+ go through the appointment page's own Insurance Card.
  onAddInsuranceClick() {
    const dialog = this.dialog.open(InsuranceDialogComponent, {
      panelClass: 'dialog-form',
      data: {
        patient: this.patient,
        appointment: this.appointment,
        insurances: this.allInsurances,
      },
    })

    dialog.afterClosed().subscribe((insurance) => {
      if (insurance) {
        this.pendingRevealInsuranceId = insurance.id
        this.patientDataService.insurancesChanged$.next()
      }
    })
  }

  onEditInsuranceClick(insurance: PatientInsurance) {
    const dialog = this.dialog.open(InsuranceDialogComponent, {
      panelClass: 'dialog-form',
      data: {
        patient: this.patient,
        appointment: this.appointment,
        insurance,
        insurances: without(this.allInsurances, insurance),
      },
    })

    dialog.afterClosed().subscribe((insurance) => {
      if (insurance) {
        this.pendingRevealInsuranceId = insurance.id
        this.patientDataService.insurancesChanged$.next()
      }
    })
  }

  onPullEligibilityClick(insurance: PatientInsurance) {
    const data: PullEligibilityDialogInput = {
      patient: this.patient,
      appointment: this.appointment,
      insurances: this.allInsurances,
      insurance,
      insuranceType: this.resolveThisVisitType(insurance),
    }
    const dialog = this.dialog.open(PullEligibilityDialogComponent, {
      panelClass: 'dialog-form',
      width: '500px',
      data,
    })
    dialog
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        tap(() => {
          this.appointmentDataService.appointmentUpdated$.next()
        }),
        concatMap(() => this.crudService.index(PatientInsurance, { patientId: this.patient.id })),
        map(insurances => insurances.find(({ id }) => id === insurance.id)),
        filter(Boolean),
        tap((latestInsurance) => {
          if (latestInsurance?.eligibilityId) {
            const resolvedType = this.resolveThisVisitType(latestInsurance)
            if (resolvedType === PatientInsuranceType.PRIMARY) {
              this.estimateService.primaryInsurance$.next(latestInsurance)
              this.patientDataService.insurancesChanged$.next()
            } else if (resolvedType === PatientInsuranceType.SECONDARY) {
              this.estimateService.secondaryInsurance$.next(latestInsurance)
              this.patientDataService.insurancesChanged$.next()
            }
          }
        }),
      )
      .subscribe({
        error: (err: PatientPalRestError) => {
          const errorMessage =
            get(err, 'error.errors[0].message') ||
            err.error?.message ||
            'Please try again.'
          this.progressService.snack(`Failed to pull eligibility. ${errorMessage}`)
        },
      })
  }

  openInsuranceHistoryDialog(insurance: PatientInsurance): void {
    this.dialog.open(PatientInsuranceHistoryDialogComponent, {
      panelClass: 'dialog-form',
      width: '800px',
      data: {
        insurance,
      },
    })
  }

  deleteInsurance(insurance: PatientInsurance): void {
    this.confirm
      .confirm('patient-details.insurance-card.confirm-delete')
      .pipe(
        takeUntil(this.onDestroy$),
        filter((yes) => yes),
        concatMap(() =>
          this.crudService.delete(insurance, {
            appointmentId: this.appointment?.id,
          }),
        ),
        tap(() => {
          this.patientDataService.insurancesChanged$.next()
        }),
      )
      .subscribe()
  }
}
